import { EventEmitter, Injectable, Injector } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CallNotificationDialogComponent } from '@components/dialogs/call-notification-dialog/call-notification-dialog.component';
import { CallNotificationInfoComponent } from '@components/dialogs/call-notification-info/call-notification-info.component';
import { SelectTeamsDialogComponent } from '@components/dialogs/select-teams-form/select-teams-dialog.component';
import { ToggleChatContainerE } from '@enums/attendances.enum';
import { ContactSourceE } from '@enums/contact-source.enum';
import { DailerMessageE } from '@enums/dialer-message.enum';
import { UserHelperService } from '@helpers/user-helper.service';
import { ContactI } from '@interfaces/contact.interface';
import { CreateTabulationI } from '@interfaces/tabulation.interface';
import { TeamsI } from '@interfaces/teams.interface';
import { UserAttributesI, UserSocketLoginI } from '@interfaces/user.interface';
import { CallHistoryService } from '@modules/call-history/call-history.service';
import { TimelineStoreService } from '@modules/chat/store/timeline-store.service';
import { GlobalStoreService } from '@modules/global/store/global-store.service';
import { PabxService } from '@modules/pabx/pabx.service';
import { TeamsService } from '@modules/teams/teams.service';
import { ContactsService } from '@services/contacts/contacts.service';
import { CallsProtocolHttpService } from '@services/protocol/calls-protocol-service';
import { TabulationService } from '@services/tabulation/tabulation.service';
import { TabulationDialogService } from '@services/tabulation/tabulation-dialog.service';
import { WebSocketInterface as SipWebsocket, UA, debug, C } from 'jssip';
import { EndEvent, IncomingEvent, OutgoingEvent, RTCSession } from 'jssip/lib/RTCSession';
import {
  ConnectedEvent,
  DisconnectEvent,
  RegisteredEvent,
  RTCSessionEvent,
  UAConfiguration,
  UnRegisteredEvent,
} from 'jssip/lib/UA';
import * as moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, filter, firstValueFrom, interval, pairwise, ReplaySubject, Subscription, take } from '@node_modules/rxjs';
import { v4 as uuidv4 } from 'uuid';
import { dialerStatusMessage } from './constants/chat-dialer.const';
import { SipErrorsE } from './enums/sip-errors.enum';
import { DialerMessageI, DialerStatusI, DialerUpdateContactI, SipDataI } from './interfaces/chat-dialer.interface';
import { BlacklistTypeE } from '@modules/blacklist/enums/blacklist-type.enum';
import { PabxEventCallAttended } from '@omnitypes/pabx-event.type';
import { ChatSocketService } from '@events/chat/chat-socket.service';
import { MonitoringActionsE } from '@modules/monitoring/enums/monitoring-actions.enum';
import { Router } from '@angular/router';
import { RoutesEnum } from '@enums/routes.enum';
import { ParametersHttpService } from '@services/parameters/parameters-http.service';
import { ParameterTypeE } from '@enums/parameter-type.enum';
import { DialerCampaignService } from '@modules/dialer-campaign/dialer-campaign.service';
import { ToastrService } from '@node_modules/ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CallHistoryI } from '@interfaces/call-history.interface';
import { CallHistoryManagerService } from './call-history-manager.service';
import { UserStoreService } from '@modules/chat/store/user-store.service';
import { DesktopNotificationService } from '@services/desktop-notification/desktop-notification.service';
import { isBlacklisted } from '@helpers/blacklist.helper';
import { BlacklistUseE } from '@modules/blacklist/enums/blacklist-use.enum';
import { ReportPauseService } from '@modules/reports/report-pause/report-pause.service';
import { ErrorAlertDialogComponent } from '@components/dialogs/error-alert-dialog/error-alert-dialog.component';

const MAX_PABX_CONNECTION_RETRIES = 10;
const MAX_PABX_CONNECTION_RETRIES_RANGE = 10000;

const DTMF_OPTIONS = { transportType: C.DTMF_TRANSPORT.RFC2833 };

// Metalfrio Solutions — customer gated for SIP event logging (jsSIP debug + backend sip-log
// capture) during the PABX-side call-rejection investigation requested by the PABX team.
const SIP_LOG_ENABLED_CUSTOMER_ID = '6926ee40b664b9da0f0a95e2';

@Injectable({
  providedIn: 'root',
})
export class SipService {
  public onMakeCallEvent: EventEmitter<string> = new EventEmitter();

  public session!: RTCSession;
  private sipSocket!: SipWebsocket;
  private server: string = '';
  private user = this.userHelper.getSocketLoginData();
  private replaySubject = new ReplaySubject();

  private ramal: string = '';
  private password: string = '';
  private host: string = '';
  public port: string = '';
  public uri: string = '';

  public customerId!: any;
  public userId!: any;

  public dialerToggle = ToggleChatContainerE.DIALER;
  public onTab = '';

  dialogRef: any;
  timerClock: any;

  public sipTimestamp!: number;
  public sipType!: string;
  public sipSRC!: string;
  public sipDST!: string;
  public sipDate!: string;
  public sipHourIn!: number;
  public sipHourOn?: string | null;
  public sipHourOut!: number;
  public sipDuration!: string;
  public sipStatus!: string;
  private regSip = false;
  public RingAudio!: HTMLAudioElement;
  public displayControl = new FormControl('', []);
  public sipControlSubscription!: Subscription;
  public logoutSubscription!: Subscription;
  public timerCall: string = '';
  public sipChannel = false;
  public channel = false;
  public antiDup = false;
  public currentSessionId = '';
  public currentContactNumber = '';
  private externalCallId?: string | null = null;
  private queueId: string = '';
  private teamByQueue?: TeamsI;
  private initialized = false;
  private useModulePabx: boolean = false;
  private _isRamalToRamalCall = false;

  public updateContactDetail$ = new BehaviorSubject<DialerUpdateContactI | undefined>(undefined);
  private messageSubject = new BehaviorSubject<DialerMessageI | null>(null);
  private audioSubject = new BehaviorSubject<MediaStream | null>(null);
  private onlineSubject = new BehaviorSubject<boolean>(false);
  private onCallSubject = new BehaviorSubject<boolean>(false);
  private callStartedAt = new BehaviorSubject<number>(0);
  private isTransferedFromContact = new BehaviorSubject<boolean>(false);
  private dialerStatusSubject = new BehaviorSubject<DialerStatusI | undefined>(undefined);
  private lastDialerStatusSubject = new BehaviorSubject<DialerStatusI | undefined>(undefined);
  private badConnectionQuality = new BehaviorSubject<boolean>(false);

  public online$ = this.onlineSubject.asObservable();
  private registered$ = new BehaviorSubject<boolean>(false);
  public onCall$ = this.onCallSubject.asObservable();
  public callStartedAt$ = this.callStartedAt.asObservable();
  public audio$ = this.audioSubject.asObservable();
  public message$ = this.messageSubject.asObservable();
  public dialerStatus$ = this.dialerStatusSubject.asObservable();
  public lastDialerStatus$ = this.lastDialerStatusSubject.asObservable();
  public badConnectionQuality$ = this.badConnectionQuality.asObservable();

  private ua!: UA;
  private sessionAudio = new Audio();
  private isNewPabxValue: boolean = false;

  private checkConnectionQualityTimeout: any = false;
  private isExternalTransfer: boolean = false;

  private optionsStatusMessage = dialerStatusMessage;

  private retryConnectionCleaner = interval(MAX_PABX_CONNECTION_RETRIES_RANGE);
  private retryConnectionCount = 0;

  private chatSocketService!: ChatSocketService;
  private finishingSubject = new BehaviorSubject<boolean>(false);
  public finishing$ = this.finishingSubject.asObservable();

  // PABX keeps only the first REGISTER per AOR (extension); this per-tab id +
  // channel let a click-to-call tab ask the others to release it before dialing.
  private readonly tabId = uuidv4();
  private readonly sipTabChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('omni-sip') : null;

  // Never inject ToastrService here: this service is built during APP_INITIALIZER (AppInitializer ->
  // SipConnectionManager -> SipService), and ngx-toastr's Overlay grabs ApplicationRef in its constructor.
  // Resolving it that early freezes an unusable ref on the root Overlay singleton and kills every toast
  // in the session. Resolve on demand instead.
  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  constructor(
    private userHelper: UserHelperService,
    private callHistoryService: CallHistoryService,
    private contactsService: ContactsService,
    private teamsService: TeamsService,
    private dialog: MatDialog,
    private callsProtocolService: CallsProtocolHttpService,
    private spinner: NgxSpinnerService,
    private pabxService: PabxService,
    private globalStore: GlobalStoreService,
    private tabulationService: TabulationService,
    private timelineStoreService: TimelineStoreService,
    private injector: Injector,
    private router: Router,
    private readonly parametersService: ParametersHttpService,
    private readonly dialerCampaignService: DialerCampaignService,
    private readonly callHistoryManager: CallHistoryManagerService,
    private readonly userStoreService: UserStoreService,
    private readonly reportPauseService: ReportPauseService,
    private readonly translate: TranslateService,
    private readonly tabulationDialogService: TabulationDialogService,
    private readonly desktopNotificationService: DesktopNotificationService
  ) {
    this.RingAudio = new Audio();
    this.RingAudio.src = '../../../../assets/audio/ring.mp3';
    this.RingAudio.loop = true;
    this.RingAudio.muted = true;
    this.RingAudio.play();

    this.globalStore.customerSettings$.subscribe(async (customerEnabledModules) => {
      this.useModulePabx = !!customerEnabledModules?.useModulePabx;
    });

    this.retryConnectionCleaner.subscribe(() => {
      this.retryConnectionCount = 0;
    });

    //Garantia que o websocket não ficará em "loop" de reconexão
    this.online$.subscribe((online) => {
      //Verifica conexões bem sucedidas
      if (online) {
        this.retryConnectionCount++;
        //Caso efetivou conexões com sucesso mais vezes que o permitido dentro do intervalo de tempo definido, força a desconexão
        if (this.retryConnectionCount >= MAX_PABX_CONNECTION_RETRIES) {
          //timeout necessário para caso a tentativa de conexão esteja "em curso", dispensa callback
          setTimeout(() => {
            this.sipLogout();
          }, 1000);
        }
      }
    });

    this.subscribeDialerStatus();
    this.subscribeDisplayControl();
    this.enableDebugLogs();
    this.setupSipTabCoordination();
  }

  public openChatDialerForm(action: MonitoringActionsE, ramal: string, specialMessage: string) {
    const url = `${RoutesEnum.PANEL}/${RoutesEnum.CHAT}/dial/`;
    if (action === MonitoringActionsE.PABX_LISTEN) {
      const phone = [action.toString(), ramal].join('|');
      this.router.navigate([url + phone]);
      this.spinner.show();
    } else if (action === MonitoringActionsE.PABX_WHISPER) {
      const phone = [action.toString(), ramal].join('|');
      this.router.navigate([url + phone]);
      this.spinner.show();
    }
  }

  public clearLastStatus() {
    this.lastDialerStatusSubject.next(undefined);
  }

  public get dialerStatus() {
    return this.dialerStatusSubject.value;
  }

  public get isRamalToRamalCall() {
    return this._isRamalToRamalCall;
  }

  public set team(team: TeamsI) {
    this.updateDialerStatus({ team });
  }

  public updateDialerStatus(value: DialerStatusI) {
    this.dialerStatusSubject.next({ ...this.dialerStatusSubject.value, ...value });
  }

  public updateContactDetail(value: DialerUpdateContactI | null) {
    this.updateContactDetail$.next({ ...this.updateContactDetail$.value, ...value });
  }

  private clearContactDetail() {
    this.updateContactDetail$.next(undefined);
  }

  private subscribeDialerStatus() {
    this.dialerStatus$.pipe(pairwise()).subscribe(([prev, current]) => {
      const updatedContact = prev?.contact?._id !== current?.contact?._id;
      const updateTeam = prev?.team?._id !== current?.team?._id;
      const updateProtocol = prev?.protocol?._id !== current?.protocol?._id;
      if (current?.inCall && (updatedContact || updateTeam || updateProtocol)) {
        this.updateContactDetail({
          contact: current?.contact,
          team: current?.team,
          protocol: current?.protocol,
        });
      }

      if (updatedContact && current?.contact) {
        this.timelineStoreService.setContact(current.contact);
      }
    });
  }

  private subscribeDisplayControl() {
    this.displayControl.valueChanges.subscribe(() => {
      const numberCall = this.dialerStatus?.numberCall ?? '';
      if (numberCall.length <= 0) {
        return;
      }

      const callInProgress = this.dialerStatus?.callInProgress;
      //Se trocar de valor depois de finalizar a chamada os dados do status do discador devem ser limpos
      if (!callInProgress && numberCall != this.displayControl.value) {
        this.clearDialerCallData();
      }
    });
  }

  private loadUserData(newUser?: UserSocketLoginI | void) {
    this.user = newUser;
    this.port = this.user?.ramalPort?.length ? this.user?.ramalPort : '8089';
    this.uri = this.user?.ramalURI ?? '';
    this.ramal = this.user?.ramal ?? '';
    this.password = this.user?.ramalPassword ?? '';
    this.host = this.user?.ramalHost ?? '';
    this.server = this.host;
  }

  private isNewPabx() {
    return this.useModulePabx;
  }

  async loadSipSocket() {
    this.isNewPabxValue = this.isNewPabx();
    if (this.isNewPabxValue) {
      return new SipWebsocket(`wss://${this.host}:${this.port}/asterisk/ws`);
    }
    return new SipWebsocket(`wss://${this.host}:${this.port}/ws`);
  }

  public isAdminUserLoggedIn() {
    const userAttributes = this.userHelper.getUserAttributes()!;
    const adminUserLoggedIn = userAttributes?.admin_user;
    return !!adminUserLoggedIn;
  }

  private injectChatSocketService(): void {
    // injeção tardia do chatSocketService para evitar circular dependency com SipService
    if (!this.chatSocketService) {
      this.chatSocketService = this.injector.get(ChatSocketService);
    }
  }

  public async init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    try {
      if (this.isAdminUserLoggedIn()) {
        return;
      }
      this.injectChatSocketService();
      this.loadUserData(this.userHelper.getSocketLoginData());
      this.userHelper.subscribeUserChange((newUser: UserAttributesI) => {
        if (newUser) {
          this.loadUserData(this.userHelper.attributesAsUserSocketLoginI(newUser));
        }
      });

      this.sessionAudio.autoplay = true;

      this.audio$.subscribe((audio) => {
        if (audio) {
          this.sessionAudio.srcObject = audio;
        }
      });

      if (this.user?.isPhone === 'true') {
        this.sipSocket = await this.loadSipSocket();
        const configuration: UAConfiguration = {
          sockets: [this.sipSocket],
          uri: `sip:${this.ramal}@${this.host}`,
          contact_uri: this.uri,
          authorization_user: this.ramal,
          password: this.password,
          register: true,
          connection_recovery_max_interval: 6,
          connection_recovery_min_interval: 2,
        };

        if (this.isNewPabxValue) {
          const checkAuthRamal = await this.pabxService.verifyRamal(this.ramal, this.password, this.host);

          if (checkAuthRamal == true) {
            if (this.regSip == false) {
              this.ua = new UA(configuration);
            }
          } else {
            this.notificationAuth();
          }
        } else {
          this.ua = new UA(configuration);
        }

        this.listeningMessages();

        /* --------- Eventos Sip ----------- */
        this.ua.on('connected', (event: ConnectedEvent) => {
          this.emitSipLog('connected', event);
          this.messageSubject.next({
            message: DailerMessageE.CONNECTED,
          });
          this.onlineSubject.next(true);
        });

        this.ua.on('disconnected', (event: DisconnectEvent) => {
          this.emitSipLog('disconnected', event);
          this.onlineSubject.next(false);
          if (this.ua.status !== 1) {
            this.onlineSubject.next(false);
          }
        });

        this.ua.on('registered', (event: RegisteredEvent) => {
          this.registered$.next(true);
          this.emitSipLog('registered', event);
          this.messageSubject.next({
            message: DailerMessageE.REGISTERED,
          });
          this.regSip = true;
        });

        this.ua.on('unregistered', (event: UnRegisteredEvent) => {
          this.registered$.next(false);
          this.emitSipLog('unregistered', event);
          this.regSip = false;
          this.messageSubject.next({
            message: DailerMessageE.UNREGISTERED,
          });
        });

        this.ua.on('registrationFailed', (event: UnRegisteredEvent) => {
          this.emitSipLog('registrationFailed', event);
          this.onlineSubject.next(false);
          this.ua.stop();
          this.ua.start();
        });

        this.ua.on('newRTCSession', async (event: RTCSessionEvent) => {
          // Extract only serializable fields; the raw event carries RTCSession/RTCPeerConnection
          // with circular refs that make the sip-log POST body fail JSON.stringify and drop silently.
          this.emitSipLog('newRTCSession', {
            originator: event.originator,
            from: event.request?.from?.uri?.user,
            to: event.request?.to?.uri?.user,
            displayName: event.request?.from?.display_name,
            queueId: event.request?.getHeader('Queueid'),
            uniqueId: event.request?.getHeader('Uniqueid'),
            transferType: event.request?.getHeader('Transfertype'),
            callId: (event.request as any)?.call_id,
          });
          this.clearDataOnNewCall();

          this.session = event.session;
          this.currentSessionId = event.session.id;
          const teamIdFromRamal = event.request.getHeader('Queueid');
          this.externalCallId = this.activeCallUniqueId || event.request.getHeader('Uniqueid');
          this.queueId = teamIdFromRamal || '';
          this.isExternalTransfer = event.request.getHeader('Transfertype') === 'EXTERNAL';
          const transferedNumber = event.request.getHeader('Transfercontent');

          let phoneNumber = event.request.from.uri.user;
          this.sipTimestamp = new Date().getTime();
          this.currentContactNumber = phoneNumber;

          this.sipDate = String(this.sipTimestamp);
          this.sipType = event.originator;

          if (!this.isExternalTransfer && teamIdFromRamal) {
            await this.setTeamByPabxQueue(teamIdFromRamal);
          }

          if (event.originator == 'local') {
            this.sipSRC = this.ramal;
            this.sipDST = event.request.to.uri.user;
            //create the active call initialization record here,
            // incoming calls will be created when the user accepts the call
            await this.handleCreatingInitiatedCallHistory();
          }

          if (this.isExternalTransfer && transferedNumber) {
            phoneNumber = transferedNumber;
          }

          if (event.originator === 'remote') {
            this.sipDST = this.ramal;
            this.sipSRC = phoneNumber;
            this.isAnonymousCall = this.isAnonymousIncoming(event, phoneNumber);

            if (this.isAnonymousCall) {
              // Origem anônima: mascara o remetente (sem número discável nem contato).
              phoneNumber = 'anonymous';
              this.sipSRC = 'anonymous';
              this.currentContactNumber = 'anonymous';
            }

            const data = {
              display_name: this.isAnonymousCall ? 'Anônimo' : event.request.from.display_name,
              number: phoneNumber,
            };

            if (!this.isAnonymousCall) {
              phoneNumber = phoneNumber.replace(/[\+\-\s\(\)]/g, '');
              phoneNumber = phoneNumber.replace(/^0+/, '');

              while (phoneNumber.length > 11 && phoneNumber.startsWith('55')) {
                phoneNumber = phoneNumber.slice(2);
              }

              phoneNumber = phoneNumber.replace(/^0+/, '');
              phoneNumber = '55' + phoneNumber;
            }
            this._isRamalToRamalCall = this.isAnonymousCall ? false : this.isRamalNumber(phoneNumber);

            this.messageSubject.next({
              message: DailerMessageE.RECEIVED_CALL,
              data,
            });
          }

          /* verifica se a chamada foi iniciada */
          if (this.session.connection) {
            this.session.connection.ontrack = (evt: RTCTrackEvent) => {
              this.audioSubject.next(evt.streams[0]);
            };

            this.checkConnectionQualityTimeout = setInterval(() => {
              if (this.session && this.session.connection) {
                this.checkConnectionQuality(this.session.connection);
              }
            }, 1000);
          }

          /* Eventos de chamada */
          this.session.on('ended', async (data: EndEvent) => {
            this.emitSipLog('session-ended', {
              direction: this.session?.direction,
              originator: data.originator,
              cause: data.cause,
              reason: (data as any)?.message?.headers?.Reason?.[0]?.raw,
              from: this.sipSRC,
              to: this.sipDST,
              queueId: this.queueId,
              uniqueId: this.externalCallId,
            });
            // resolved once per call so start/end pause gates never diverge mid-flow
            const autoPauseTabulation = await this.isAutoPauseTabulationEnabled();
            const mandatoryTelephony = !!(await firstValueFrom(
              this.parametersService.mandatoryTabulationParameterTelephony$
            ));
            try {
              this.finishingSubject.next(true);
              this.clearCheckConnectionQuality();

              this.userId = this.user?._id;
              this.customerId = this.user?.customerId;
              this.sipHourOut = new Date().getTime();
              const durationMs = this.sipHourOut - (this.sipHourIn || this.sipHourOut);
              this.sipDuration = String(durationMs) ?? '0';

              this.updateDialerStatus({
                inCall: false,
                callDuration: moment.utc(durationMs).format('HH:mm:ss'),
              });
              this.displayControl.setValue('');
              const sipData = this.buildObjectSipCurrentData();
              const dialerStatus = this.dialerStatus;

              const cause = this.getErrorCause(data);

              if (cause != SipErrorsE[SipErrorsE.NORMAL_CLEARING]) {
                this.toasterError(cause);
              }

              if (!this.isTransferedFromContact.value) {
                if (await this.useTabulation()) {
                  if (autoPauseTabulation) {
                    this.createPausePabx();
                  }

                  // call context shown in the tabulation modal so the agent knows which call to tabulate
                  const callInfo = {
                    contactName: dialerStatus?.contact?.name,
                    number: dialerStatus?.contact?.telephone ?? (sipData.type === 'local' ? sipData.dst : sipData.src),
                    protocolNumber: dialerStatus?.protocol?.protocolNumber,
                    duration: moment.utc(durationMs).format('HH:mm:ss'),
                  };

                  if (mandatoryTelephony) {
                    // persist the call and flag it pending in the backend before the modal, so a page
                    // refresh mid-tabulation can restore it on next login
                    const call = await this.updateOrCreateCallHistory(sipData, dialerStatus);
                    if (call?._id) {
                      await this.callHistoryManager.setPendingTabulation({ callHistoryId: call._id, ...callInfo });
                      // pending state lives in the backend now; drop the local unfinished-call marker so
                      // checkForUnfinishedCalls won't close it as disconnected on refresh
                      this.callHistoryManager.clear();
                    }

                    const infoTabulation = await this.tabulationDialogService.openCallTabulation(callInfo);
                    if (call?._id && infoTabulation?.tabulationIds?.length) {
                      await this.callHistoryManager.concludeTabulationById(call._id, infoTabulation);
                    }
                  } else {
                    const infoTabulation = await this.tabulationDialogService.openCallTabulation(callInfo);
                    this.updateOrCreateCallHistory(sipData, dialerStatus, infoTabulation).then(() =>
                      this.callHistoryManager.clear()
                    );
                  }
                } else {
                  this.updateOrCreateCallHistory(sipData, dialerStatus).then(() => this.callHistoryManager.clear());
                }
              } else {
                this.callHistoryManager.clear();
              }

              this.replaySubject.next(null);
              this.messageSubject.next({
                message: DailerMessageE.END_CALL,
              });

              this.updateDialerStatus({
                callEnded: true,
              });
            } catch (error) {
              this.toasterError('UNKNOWN_ERROR');
              console.error(error);
            } finally {
              if (autoPauseTabulation && (await this.useTabulation()) && !this.isTransferedFromContact.value) {
                this.endPausePabx();
              }

              this.clearContactDetail();
              this.clearCallData();

              this.finishingSubject.next(false);
            }
          });

          this.session.on('failed', async (data: EndEvent) => {
            this.clearCheckConnectionQuality();
            this.emitSipLog('session-failed', {
              direction: this.session?.direction,
              originator: data.originator,
              cause: data.cause,
              reason: (data as any)?.message?.headers?.Reason?.[0]?.raw,
              from: this.sipSRC,
              to: this.sipDST,
              queueId: this.queueId,
              uniqueId: this.externalCallId,
            });

            this.sipDate = String(new Date().getTime());
            this.userId = this.user?._id;
            this.customerId = this.user?.customerId;
            this.sipStatus = data.cause;
            this.sipDuration = '0';

            if (this.session.direction == 'incoming') {
              this.sipChannel = false;
              this.antiDup = false;
              this.cancelCall();
              this.ringCall(false);
              this.hangup();
            }

            if (this.session.direction == 'outgoing') {
              this.antiDup = false;

              this.failCall(this.formatErrorMsg(this.sipStatus));
            }

            const sipData = this.buildObjectSipCurrentData();
            await this.handleCallHistoryEventFail(sipData, this.dialerStatus);
            this.clearCallData();
            this.replaySubject.next(null);

            this.updateDialerStatus({
              callEnded: true,
              callDuration: '00:00:00',
              protocol: this.dialerStatus?.protocol,
            });

            this.messageSubject.next({
              message: DailerMessageE.FAILED,
            });
          });

          this.session.on('peerconnection', (data) => {
            this.session.connection.ontrack = (evt: RTCTrackEvent) => {
              this.audioSubject.next(evt.streams[0]);
            };
          });

          this.session.on('confirmed', (data: IncomingEvent | OutgoingEvent) => {
            this.messageSubject.next({
              message: DailerMessageE.CALL,
            });
          });

          this.session.on('muted', () => {
            this.messageSubject.next({
              message: DailerMessageE.MUTED,
            });
          });

          this.session.on('unmuted', () => {
            this.messageSubject.next({
              message: DailerMessageE.UNMUTED,
            });
          });

          this.session.on('hold', () => {
            this.messageSubject.next({
              message: DailerMessageE.HOLD,
            });
          });

          this.session.on('unhold', () => {
            this.messageSubject.next({
              message: DailerMessageE.UNHOLD,
            });
          });
        });

        this.ua.start();
        (<any>this.ua).on('ended', (event: any) => {
          this.emitSipLog('ended', event);
        });
        (<any>this.ua).on('failed', (event: any) => {
          this.emitSipLog('failed', event);
        });

        this.ua.on('newMessage', (e) => {
          try {
            if (e?.originator === 'remote') {
              e?.message?.accept();
              const { event, ramal } = JSON.parse(e?.request?.body);
              if (event === 'call_attended' && ramal === this.ramal) {
                /* disparado quando uma ligação ativa e atendida  */
                this.confirmCall();
              }
            }
          } catch (err) {
            console.error(err);
          }
        });
      }

      //Eventos de socket omnismart vindo via webhook da public API
      this.chatSocketService.pabxCallAttended$.subscribe(async (data: PabxEventCallAttended | null) => {
        if (data?.idCampaing && data?.idExtra) {
          const campaign = await firstValueFrom(this.dialerCampaignService.getById(data?.idCampaing));
          const external = await firstValueFrom(
            this.dialerCampaignService.findOneExternal(data?.idCampaing, data?.idExtra)
          );

          const campaignMailing = external
            ? external
            : (campaign?.mailing as any[])?.find((mailing) => mailing?.idExternal == data?.idExtra);

          const team = (campaign?.team as TeamsI)?._id
            ? (campaign?.team as TeamsI)
            : await firstValueFrom(this.teamsService.getTeamById(campaign?.team as string));

          if (campaign && campaignMailing) {
            this.updateDialerStatus({
              team,
              campaign: {
                ...campaign,
                mailing: [campaignMailing],
                integration: { ...campaign.integration, mailing: [] },
              },
              campaignMailing,
            });

            if (campaignMailing.customFieldsContact && campaignMailing.customFieldsContact.length) {
              try {
                await this.updateContactByNumber(data.origin, {
                  customFields: campaignMailing.customFieldsContact,
                });
              } catch (error) {
                console.error(error);
              }
            }
          }
        }
      });

      this.logoutSubscription = this.globalStore.getLogout().subscribe((logout: boolean | undefined) => {
        if (logout) {
          this.sipLogout();
        }
      });
    } catch (error) {
      console.error(error);
      this.clearContactDetail();
      this.clearCallData();
    }
  }

  private toasterError(message: string) {
    const toaster = this.injector.get(ToastrService);
    const translate = this.injector.get(TranslateService);
    if (toaster && translate) {
      toaster.error(translate.instant(message ?? 'UNKNOWN_ERROR'), undefined, { closeButton: true });
    }
  }

  private normalizePhoneForCompare(value?: string): string {
    if (!value) {
      return '';
    }
    let digits = value.replace(/\D+/g, '');
    digits = digits.replace(/^0+/, '');
    while (digits.length > 11 && digits.startsWith('55')) {
      digits = digits.slice(2);
    }
    return digits;
  }

  private contactHasOnlyWhatsappLinkId(contact?: ContactI | null): boolean {
    if (!contact?.whatsappLinkId) {
      return false;
    }
    const widNormalized = this.normalizePhoneForCompare(contact.whatsappLinkId);
    if (!widNormalized) {
      return false;
    }
    const telephones = (contact.telephones ?? []).filter((tel) => !!tel);
    if (telephones.length === 0) {
      return true;
    }
    return telephones.every((tel) => this.normalizePhoneForCompare(tel) === widNormalized);
  }

  private buildObjectSipCurrentData(): SipDataI {
    return {
      src: this.sipSRC,
      dst: this.sipDST,
      type: this.sipType,
      status: this.sipStatus,
      duration: this.sipDuration,
      date: this.sipDate,
      hourIn: this.sipHourIn,
      hourOut: this.sipHourOut,
      timestamp: this.sipTimestamp,
      queueId: this.queueId,
      externalCallId: this.externalCallId,
      anonymous: this.isAnonymousCall,
    };
  }

  private isUseCallHistory(sipData: SipDataI, dialerStatus?: DialerStatusI): boolean {
    // Chamada anônima sempre gera callhistory (com flag), mesmo sem número discável/contato.
    if (sipData.anonymous) {
      return true;
    }

    const isOrigin = sipData.type === 'local';
    let phoneNumber = (!isOrigin ? sipData.src : sipData.dst) || '';
    phoneNumber = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;

    if (this.isRamalNumber(phoneNumber) || dialerStatus?.enabledHiddenNumberCall) {
      return false;
    }
    return true;
  }

  private async handleCreatingInitiatedCallHistory(): Promise<void> {
    const sipData = this.buildObjectSipCurrentData();
    const currentdialerStatus = this.dialerStatus;
    const dialerStatus = {
      ...currentdialerStatus,
      team: currentdialerStatus?.team ?? this.teamByQueue ?? undefined,
    };

    if (!this.isUseCallHistory(sipData, dialerStatus)) {
      return;
    }

    await this.generateProtocol();
    dialerStatus.protocol = this.dialerStatus?.protocol ?? dialerStatus.protocol;

    this.callHistoryManager.createInitiatedByDataSip(sipData, dialerStatus);
  }

  private async handleCallHistoryEventFail(sipData: SipDataI, dialerStatus?: DialerStatusI) {
    await this.updateOrCreateCallHistory(sipData, dialerStatus);
    this.callHistoryManager.clear();
  }

  private async updateOrCreateCallHistory(
    sipData: SipDataI,
    dialerStatus?: DialerStatusI,
    infoTabulation?: CreateTabulationI
  ): Promise<CallHistoryI | undefined> {
    if (!this.isUseCallHistory(sipData, dialerStatus)) {
      return;
    }

    await this.generateProtocol();
    if (dialerStatus) {
      dialerStatus.protocol = this.dialerStatus?.protocol ?? dialerStatus.protocol;
    }

    return await this.callHistoryManager.createOrUpdateByDataSip(sipData, dialerStatus, infoTabulation);
  }

  private confirmCall() {
    this.handleConfirmCall(new Date());
    this.messageSubject.next({
      message: DailerMessageE.IN_CALL,
    });
  }

  private handleConfirmCall(startDate: Date) {
    const date = startDate;
    this.sipDate = String(date.getTime());
    this.sipHourIn = date.getTime();
    this.sipStatus = 'confirmed';
    this.timerStart();
    this.onCallSubject.next(true);
    this.callStartedAt.next(date.getTime());

    const sipData = this.buildObjectSipCurrentData();
    const call = this.callHistoryManager.buildCallData(sipData, this.dialerStatus);
    this.callHistoryManager.updateTempLocalStorage(call);

    if (sipData.type === 'remote') {
      //Receptives Calls creates the call history initiated when the user clicks answer
      void this.handleCreatingInitiatedCallHistory();
    }
  }

  private clearDataOnNewCall() {
    this.callHistoryManager.clear();
    this.isAnonymousCall = false;
  }

  private clearCallData() {
    this.sipHourOn = null;
    this.isExternalTransfer = false;
    this.activeCallUniqueId = '';
    this.externalCallId = null;
    this.queueId = '';
    this.teamByQueue = undefined;
    this.isTransferedFromContact.next(false);
    this.isAnonymousCall = false;
  }

  public clearDialerCallData() {
    this.lastDialerStatusSubject.next(this.dialerStatusSubject.value);
    this.dialerStatusSubject.next({
      statusMessage: '',
      inCall: false,
      callInProgress: false,
      callEnded: false,
      contact: undefined,
      callByWhatsApp: false,
    });
  }

  public listeningMessages() {
    this.sipControlSubscription = this.message$.subscribe(async (message) => {
      if (!message) {
        this.clearDialerCallData();
        this.sipHourOn = null;
        return;
      }
      let inCall = this.dialerStatus?.inCall ?? false;
      let callInProgress = this.dialerStatus?.callInProgress ?? false;

      switch (message.message) {
        case DailerMessageE.CALL:
          callInProgress = true;
          break;
        case DailerMessageE.IN_CALL: {
          inCall = true;
          callInProgress = true;
          this.generateProtocol();
          this.getNumberRegion();
          break;
        }
        case DailerMessageE.RECEIVED_CALL:
          if (this.antiDup != true) {
            // Anônima não tem número discável → não cria contato (null pula a criação em notificationCall).
            const contact = this.isAnonymousCall ? null : await this.findOrCreateContactByNumber(message.data.number);
            if (contact && isBlacklisted(contact, BlacklistTypeE.INCOMING, BlacklistUseE.CALL)) {
              this.emitSipLog('call-rejected-blacklist', {
                from: this.sipSRC,
                to: this.sipDST,
                queueId: this.queueId,
                uniqueId: this.externalCallId,
                contactId: contact?._id,
              });
              this.hangup();
            } else {
              this.clearDialerCallData();
              if (this.teamByQueue) {
                this.updateDialerStatus({ team: this.teamByQueue });
              }
              this.antiDup = true;
              // Atendimento automático: equipe com telefonia habilitada e toggle marcado
              // atende a chamada sem exigir clique no dialog de notificação.
              const autoAnswer = !!this.teamByQueue?.webphone?.attendanceAutomaticWebphone;
              this.notificationCall(message.data, contact, autoAnswer);
              if (!autoAnswer) {
                this.ringCall(true);
              }
              this.generateProtocol();
              this.getNumberRegion();
              this.notificationMaster();
            }
          } else {
            // Incoming INVITE ignored because a call is already in progress (antiDup guard) — one of
            // the silent queue-call rejections the PABX side needs visibility into.
            this.emitSipLog('call-dropped-antidup', {
              from: this.sipSRC,
              to: this.sipDST,
              queueId: this.queueId,
              uniqueId: this.externalCallId,
            });
          }
          break;
        case DailerMessageE.HANGUP:
          //ending call
          this.timerStop();
          break;
        case DailerMessageE.END_CALL:
          this.cancelCall();
          inCall = false;
          this.onCallSubject.next(false);
          callInProgress = false;
          this.antiDup = false;
          this.displayControl.setValue('');
          this.timerStop();
          break;
        case DailerMessageE.MUTED:
        case DailerMessageE.UNMUTED:
          break;
        case DailerMessageE.FAILED:
          this.ringCall(false);
          inCall = false;
          callInProgress = false;
          this.antiDup = false;
          this.displayControl.setValue('');
          this.timerStop();
          break;
        default:
          break;
      }
      this.updateDialerStatus({
        statusMessage: this.convertStatusMessage(message.message),
        inCall,
        callInProgress,
      });
      return;
    });
  }

  private async generateProtocol(): Promise<void> {
    if (this.isRamalToRamalCall || this.dialerStatus?.protocol) return;
    try {
      const protocol = await this.fetchProtocol();
      if (protocol) this.updateDialerStatus({ protocol });
    } catch (error) {
      console.error('generateProtocol failed:', error);
    }
  }
  public fetchProtocol() {
    return firstValueFrom(this.callsProtocolService.post());
  }

  private convertStatusMessage(dialerMessage: string) {
    const statusMessage = this.optionsStatusMessage.find((status) => status.value === dialerMessage);
    return statusMessage?.name ?? '';
  }

  public async restart() {
    if (!this.initialized) {
      return;
    }
    if (this.regSip) {
      this.sipLogout();
    }
    const customerSettings = await firstValueFrom(this.globalStore.customerSettings$);
    if (!customerSettings) {
      return;
    }

    this.onlineSubject.next(false);

    if (this.ua) {
      if (this.sipControlSubscription) {
        this.sipControlSubscription.unsubscribe();
      }
    }

    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }

    void this.init();
  }

  public sipLogout() {
    if (this.ua) {
      if (this.sipSocket) {
        this.sipSocket.disconnect();
      }
      this.ua.unregister();
      this.ua.stop();
      this.initialized = false;
      this.regSip = false;
      if (this.sipControlSubscription) {
        this.sipControlSubscription.unsubscribe();
      }
    }
  }

  // Another tab (e.g. SniperSell click-to-call) is taking the ramal over — drop
  // our registration so its REGISTER wins, then ACK so it can proceed to dial.
  private setupSipTabCoordination(): void {
    this.sipTabChannel?.addEventListener('message', (event: MessageEvent) => {
      const message = event?.data;
      if (message?.type !== 'ramal-takeover' || message.by === this.tabId) {
        return;
      }
      if (this.regSip) {
        this.sipLogout();
        this.toastr.info(this.translate.instant('RAMAL_OFFLINE'));
      }
      this.sipTabChannel?.postMessage({ type: 'ramal-released', to: message.by });
    });
  }

  // Take the ramal over for this tab: broadcast the request, wait for a release
  // ACK (or a short fallback when no other tab holds it), then register + dial.
  public async takeoverRamal(): Promise<void> {
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        this.sipTabChannel?.removeEventListener('message', onReleased);
        resolve();
      };
      const onReleased = (event: MessageEvent) => {
        if (event?.data?.type === 'ramal-released' && event.data.to === this.tabId) {
          finish();
        }
      };
      this.sipTabChannel?.addEventListener('message', onReleased);
      this.sipTabChannel?.postMessage({ type: 'ramal-takeover', by: this.tabId });
      setTimeout(finish, 1500);
    });

    this.sipLogout();
    await this.init();
    await Promise.race([
      firstValueFrom(this.registered$.pipe(filter((registered) => registered), take(1))),
      new Promise((resolve) => setTimeout(resolve, 8000)),
    ]);
    if (this.regSip) {
      this.toastr.success(this.translate.instant('RAMAL_ONLINE'));
    }
  }

  private isRamalNumber(text?: string): boolean {
    const value = (text ?? '').trim();
    const number = value.replace(/\D+/g, '');
    const hasLetters = !!value.toLowerCase().match(/[a-z]+/);
    const isNewPabxRamalValue = this.isNewPabxValue && hasLetters;
    const result = (number.length > 0 && number.length < 8) || isNewPabxRamalValue;
    return result;
  }

  private activeCallUniqueId = '';

  /** Marca a chamada recebida em curso como de origem anônima (sem contato). */
  private isAnonymousCall = false;

  /**
   * Interceptor de teste local. Quando ligado, QUALQUER chamada recebida é
   * tratada como anônima, permitindo validar o fluxo de callhistory/timeline
   * sem contato. Ative no DevTools (console) com qualquer uma:
   *   window.__forceAnonymousIncoming = true            // sobrevive a localStorage.clear()
   *   localStorage.setItem('forceAnonymousIncoming','true')
   * Atenção: o app chama localStorage.clear() em fluxos de auth/refresh
   * (LocalStorageService), apagando a key do localStorage — por isso o
   * preferencial é a flag em window, que persiste até reload da página.
   */
  public get forceAnonymousIncoming(): boolean {
    try {
      if ((window as unknown as { __forceAnonymousIncoming?: boolean }).__forceAnonymousIncoming === true) {
        return true;
      }
      return localStorage.getItem('forceAnonymousIncoming') === 'true';
    } catch {
      return false;
    }
  }

  public setForceAnonymousIncoming(enabled: boolean): void {
    (window as unknown as { __forceAnonymousIncoming?: boolean }).__forceAnonymousIncoming = enabled;
    try {
      localStorage.setItem('forceAnonymousIncoming', enabled ? 'true' : 'false');
    } catch (error) {
      console.error('Error setting forceAnonymousIncoming flag:', error);
    }
  }

  /**
   * Detecta chamada recebida de origem anônima: source "anonymous" do PABX,
   * número privado/oculto/restrito no SIP From, ou sem dígitos discáveis.
   * O interceptor de teste (`forceAnonymousIncoming`) força true.
   */
  private isAnonymousIncoming(event: RTCSessionEvent, rawNumber?: string): boolean {
    if (this.forceAnonymousIncoming) {
      return true;
    }
    const from = event?.request?.from;
    const fromUser = from?.uri?.user ?? '';
    const fromHost = (from?.uri as { host?: string })?.host ?? '';
    const displayName = from?.display_name ?? '';
    const anonymousPattern = /anonymous|an[oô]nim|restrict|withheld|unavailable|unknown|private|n[aã]o\s*identificad/i;
    if (anonymousPattern.test(displayName) || anonymousPattern.test(fromUser) || anonymousPattern.test(fromHost)) {
      return true;
    }
    // Sem dígitos discáveis após limpeza → origem não identificável.
    return (rawNumber ?? '').replace(/\D+/g, '').length === 0;
  }

  public async call(number: string, statusInfo = {}, currentContact?: ContactI) {
    this._isRamalToRamalCall = this.isRamalNumber(number);
    if (this.antiDup != true) {
      this.antiDup = true;

      const team = await this.ensureTeam();
      if (!team) {
        this.antiDup = false;
        return;
      }

      this.activeCallUniqueId = uuidv4();
      const params = { teamId: this.dialerStatus?.team?._id || '' };
      const contact = currentContact ?? (await this.findOrCreateContactByNumber(number));

      if (this.contactHasOnlyWhatsappLinkId(contact)) {
        this.toasterError('CONTACT_WITHOUT_REAL_PHONE');
        this.antiDup = false;
        this.activeCallUniqueId = '';
        return;
      }

      const options = {
        mediaConstraints: {
          audio: true,
          video: false,
        },
        extraHeaders: [
          'OMNI-UniqueId: ' + this.activeCallUniqueId,
          'OMNI-params: ' + JSON.stringify(params),
          this.buildSipAdditionalInfoHeader(contact),
        ],
      };

      if (contact) {
        this.updateDialerStatus({
          contact,
          numberCall: number,
          callDuration: undefined,
          callByWhatsApp: false,
          ...statusInfo,
        });
      } else {
        this.updateDialerStatus({ numberCall: number, callDuration: undefined, callByWhatsApp: false, ...statusInfo });
      }
      /* Realiza a chamada */
      this.session = this.ua.call(`sip:${number}@${this.server}`, options);
      this.messageSubject.next({
        message: DailerMessageE.CALL,
      });
    }
  }

  public async callByWhatsapp(number: string, statusInfo = {}, currentContact?: ContactI) {
    this._isRamalToRamalCall = this.isRamalNumber(number);

    if (this._isRamalToRamalCall) {
      return;
    }

    if (this.antiDup != true) {
      this.antiDup = true;

      const team = await this.ensureTeam();
      if (!team) {
        this.antiDup = false;
        return;
      }

      this.activeCallUniqueId = uuidv4();
      const params = { teamId: this.dialerStatus?.team?._id || '' };
      const contact = currentContact ?? (await this.findOrCreateContactByNumber(number));
      const options = {
        mediaConstraints: {
          audio: true,
          video: false,
        },
        extraHeaders: [
          'OMNI-UniqueId: ' + this.activeCallUniqueId,
          'OMNI-params: ' + JSON.stringify(params),
          this.buildSipAdditionalInfoHeader(contact),
        ],
      };
      if (contact) {
        this.updateDialerStatus({
          contact,
          numberCall: number,
          callDuration: undefined,
          callByWhatsApp: true,
          ...statusInfo,
        });
      } else {
        this.updateDialerStatus({ numberCall: number, callDuration: undefined, callByWhatsApp: true, ...statusInfo });
      }
      const numberToWhatsapp = `##${this.prepareNumberToContact(number)}`;
      /* Realiza a chamada */
      this.session = this.ua.call(`sip:${numberToWhatsapp}@${this.server}`, options);
      this.messageSubject.next({
        message: DailerMessageE.CALL,
      });
    }
  }

  private buildSipAdditionalInfoHeader(contact?: ContactI | null): string {
    const user = this.user as UserSocketLoginI | undefined;
    const attrs = this.userHelper.getUserAttributes();
    const team = this.dialerStatus?.team;
    const teamRef = team?._id ? { _id: team._id, name: team.name } : null;
    const additionalInfo = {
      user: user
        ? {
            _id: user._id,
            name: user.name,
            email: attrs?.email,
          }
        : null,
      userLevel: user?.level ?? attrs?.level,
      customerId: user?.customerId,
      ramal: user?.ramal,
      teams: teamRef ? [teamRef] : [],
      teamCall: teamRef,
      contact: contact
        ? {
            _id: contact._id,
            name: contact.name,
            telephone: contact.telephone ?? contact.telephones?.[0],
            telephones: contact.telephones,
            email: contact.email,
            emails: contact.emails,
          }
        : null,
      protocolNumber: this.dialerStatus?.protocol?.protocolNumber,
      tabulations: [] as unknown[],
      tabulationIds: [] as string[],
    };
    return 'OMNI-additional-info: ' + JSON.stringify(additionalInfo);
  }

  public async findContactByNumber(phoneNumber: string): Promise<ContactI | undefined> {
    const number = this.prepareNumberToContact(phoneNumber);
    if (!number) {
      return;
    }
    return firstValueFrom(this.contactsService.getContactByPhone(number, (this.user as UserSocketLoginI)!.customerId));
  }

  private prepareNumberToContact(value: string): string | null {
    let number = value;

    number = number.replace(/[\+\-\s\(\)]/g, '');
    number = number.replace(/^0+/, '');

    if (this.isRamalNumber(number)) {
      return null;
    }

    // Strip DDI 55 duplicado/triplicado (bug PABX trunk) até formato nacional (10-11 dígitos)
    while (number.length > 11 && number.startsWith('55')) {
      number = number.slice(2);
    }
    number = number.replace(/^0+/, '');

    return '55' + number;
  }

  private async findOrCreateContactByNumber(phoneNumber: string): Promise<ContactI | null> {
    const number = this.prepareNumberToContact(phoneNumber);
    if (!number) {
      return null;
    }
    const body: ContactI = {
      customerId: this.customerId,
      telephone: number,
      telephones: [number],
      email: '',
      emails: [],
      name: number,
      source: ContactSourceE.Call,
    };
    const result = await this.contactsService.findOrCreateContactByPhone(number, body);
    return result;
  }

  private async updateContactByNumber(phoneNumber: string, contact: Partial<ContactI>): Promise<ContactI | null> {
    const number = this.prepareNumberToContact(phoneNumber);

    if (!number) {
      return null;
    }

    return await this.contactsService.updateContactByPhone(number, contact);
  }

  private async setTeamByPabxQueue(pabxQueueId: string) {
    try {
      const team = await firstValueFrom(this.teamsService.getTeamByPabxQueue(pabxQueueId));

      if (team) {
        this.teamByQueue = team;
      }

      return this.teamByQueue;
    } catch (error) {
      console.error('Error setting team by PABX Queue:', error);
      return;
    }
  }

  public transfer(numberDestino: string) {
    if (!this.session || this.session.isEnded()) return;
    this.isTransferedFromContact.next(!!this.dialerStatus?.contact);
    const number = `*2${numberDestino}#`;
    this.session.sendDTMF(number, DTMF_OPTIONS);
  }

  public DTMF(number: string) {
    if (!this.session || this.session.isEnded()) return;
    this.session.sendDTMF(number, DTMF_OPTIONS);
  }

  public async accept() {
    await this.applyIncomingCallTeamFallback();
    const team = await this.ensureTeam();
    if (!team) {
      // Answer aborted because no team could be resolved (queue call auto-answer or manual) —
      // surfaces as a queue-call rejection with no other trace today.
      this.emitSipLog('call-rejected-no-team', {
        from: this.sipSRC,
        to: this.sipDST,
        queueId: this.queueId,
        uniqueId: this.externalCallId,
      });
      this.hangup();
      return;
    }
    this.session.answer({
      extraHeaders: [this.buildSipAdditionalInfoHeader(this.dialerStatus?.contact)],
    });
    this.onCallSubject.next(true);
    this.callStartedAt.next(new Date().getTime());
  }

  // Incoming calls only: with no team resolved from the PABX queue, route to the team set in
  // general parameters instead of prompting the agent. Fetched fresh per call (not the cached
  // subject from initOnce, which stays stale until relogin) so admin changes apply immediately.
  private async applyIncomingCallTeamFallback(): Promise<void> {
    if (this.dialerStatus?.team) {
      return;
    }
    try {
      const param = await firstValueFrom(this.parametersService.findLastByType(ParameterTypeE.INCOMING_CALL_TEAM));
      const teamId = param?.parameter?.incomingCallTeam;
      if (!teamId) {
        return;
      }
      const team = await firstValueFrom(this.teamsService.getTeamById(teamId));
      // a team that lost telephony can no longer answer calls — fall back to the manual dialog
      if (team?.modalityTelephony) {
        this.updateDialerStatus({ team });
      }
    } catch (error) {
      console.error('applyIncomingCallTeamFallback failed:', error);
    }
  }

  private async ensureTeam(): Promise<TeamsI | null> {
    const current = this.dialerStatus?.team;
    if (current) return current as TeamsI;
    try {
      const ref = this.dialog.open(SelectTeamsDialogComponent, {
        width: '300px',
        panelClass: 'custom-dialog-v2',
        data: { filterTelephony: true },
      });
      const team = await firstValueFrom(ref.afterClosed());
      if (team) {
        this.updateDialerStatus({ team });
        return team as TeamsI;
      }
      return null;
    } catch (error) {
      console.error('ensureTeam failed:', error);
      return null;
    }
  }

  private timerStart() {
    this.timerClock = setInterval(() => {
      if (!this.dialerStatus?.inCall) {
        this.sipHourOn = this.dialerStatus?.callDuration;
      } else {
        const clockTime = new Date().getTime();
        const calcClockTime = clockTime - this.sipHourIn;
        this.sipHourOn = moment.utc(+calcClockTime).format('HH:mm:ss');
      }
    }, 1000);
  }

  private timerStop() {
    clearInterval(this.timerClock);
  }

  public hangup(): void {
    this.messageSubject.next({ message: DailerMessageE.HANGUP });
    this.antiDup = false;
    if (!this.session) return;
    try {
      this.session.terminate({
        extraHeaders: [this.buildSipAdditionalInfoHeader(this.dialerStatus?.contact)],
      });
    } catch (error) {
      console.error(error);
    }
  }

  // fetched fresh on every call end (not cached at login) so admin changes apply
  // to the next call; absent parameter or fetch error keeps legacy behavior (paused)
  private async isAutoPauseTabulationEnabled(): Promise<boolean> {
    try {
      const param = await firstValueFrom(this.parametersService.findLastByType(ParameterTypeE.AUTO_PAUSE_TABULATION));
      return param?.parameter?.enabled ?? true;
    } catch (error) {
      console.error(error);
      return true;
    }
  }

  private async createPausePabx() {
    try {
      if (
        !(
          this.user?._id &&
          // callId links the tabulation pause (ACW) to the PABX call for the team telephony report
          (await this.reportPauseService.startPausePabxByUserId(
            this.user._id,
            this.user.customerId,
            this.externalCallId ?? undefined
          ))
        )
      ) {
        this.pauseTabulationErrorAlert();
      }
    } catch (error) {
      this.pauseTabulationErrorAlert();
      console.error(error);
    }
  }

  private async endPausePabx() {
    try {
      if (
        !(this.user?._id && (await this.reportPauseService.endPausePabxByUserId(this.user._id, this.user.customerId)))
      ) {
        this.stopPauseTabulationErrorAlert();
      }
    } catch (error) {
      this.stopPauseTabulationErrorAlert();
      console.error(error);
    }
  }

  private pauseTabulationErrorAlert() {
    this.dialog.open(ErrorAlertDialogComponent, {
      width: '400px',
      data: 'PAUSE_START_TABULATION_ERROR',
    });
  }

  private stopPauseTabulationErrorAlert() {
    this.dialog.open(ErrorAlertDialogComponent, {
      width: '400px',
      data: 'PAUSE_END_TABULATION_ERROR',
    });
  }

  private async useTabulation(): Promise<boolean> {
    if (!!this.isTransferedFromContact.value || this.isRamalToRamalCall) {
      return false;
    }

    let tabulations = await firstValueFrom(this.tabulationService.tabulations$);

    return tabulations.length > 0;
  }

  public mute(): Boolean {
    if (!this.session || this.session.isEnded() || this.session.isInProgress()) return false;
    this.session.mute();
    return true;
  }

  public unmute(): Boolean {
    if (!this.session || this.session.isEnded()) return false;
    this.session.unmute();
    return true;
  }

  public hold(): Boolean {
    if (!this.session || this.session.isEnded() || this.session.isInProgress()) return false;
    this.session.hold();
    return true;
  }

  public unhold(): Boolean {
    if (!this.session || this.session.isEnded()) return false;
    this.session.unhold();
    return true;
  }

  private notificationMaster(): void {
    this.channel = false;
    // Permission is now solicited at login (OMNI-7637 banner); here we only fire when already granted.
    this.desktopNotificationService.notify({
      type: 'incomingCall',
      title: this.translate.instant('NOTIFICATIONS.DESKTOP.INCOMING_CALL'),
      body: this.dialerStatus?.numberCall,
      tag: 'incoming-call',
      autoCloseMs: 60000,
    });
  }

  public cancelCall(): void {
    this.dialogRef?.close();
    this.ringCall(false);
  }

  public ringCall(action: boolean): void {
    this.RingAudio.currentTime = 0;
    if (action == true) {
      this.RingAudio.muted = false;
      this.RingAudio.play();
    } else {
      this.RingAudio.muted = true;
      this.RingAudio.pause();
    }
  }

  public failCall(info: string): void {
    this.dialogRef = this.dialog.open(CallNotificationInfoComponent, {
      width: '500px',
      data: {
        title: 'INFO_CALL',
        info: info,
        prefix: 'STATUS',
      },
    });
    this.dialogRef.afterClosed().subscribe((result: string) => {});
  }

  private formatErrorMsg(msg: string): string {
    return msg.trim().replace(/\s+/g, '_').toUpperCase();
  }

  public async notificationCall(
    { number, display_name }: { number: string; display_name?: string },
    contact?: ContactI | null,
    autoAnswer = false
  ) {
    if (contact === undefined) {
      contact = await this.findOrCreateContactByNumber(number);
    }
    const callByWhatsApp = number.startsWith('+');

    if (contact) {
      this.updateDialerStatus({ contact, numberCall: number, callByWhatsApp });
    } else {
      this.updateDialerStatus({ numberCall: number, callByWhatsApp });
      contact = display_name ? { name: display_name } : undefined;
    }

    // Atendimento automático da equipe: atende direto, sem abrir o dialog de notificação.
    // Mesmo caminho do branch manual 'ANSWER' (sipChannel = false → accept → confirmCall).
    if (autoAnswer) {
      this.sipChannel = false;
      await this.accept();
      this.confirmCall();
      return;
    }

    const data = {
      contact,
      number,
      displayName: display_name,
    };

    this.dialogRef = this.dialog.open(CallNotificationDialogComponent, {
      disableClose: true,
      width: '500px',
      data,
    });
    this.dialogRef.afterClosed().subscribe(async (result: string) => {
      if (result == 'ANSWER') {
        this.ringCall(false);
        this.sipChannel = false;
        await this.accept();
        //Necessário executar processamento do evento de confirmar da ligação recebida por aqui por aqui
        // porque ainda não é possível receber evento do socket omnismart de chamada recebida
        this.confirmCall();
      }
      if (result == 'HANGUP') {
        this.hangup();
        this.ringCall(false);
        this.sipChannel = false;
      }
      if (result == 'SILENCE') {
        this.ringCall(false);
        this.sipChannel = true;
      }
    });
  }

  public notificationAuth(): void {
    this.toasterError('INFORM_REGISTER');
  }

  private checkConnectionQuality(peerConnection: RTCPeerConnection) {
    peerConnection.getStats(null).then((stats) => {
      let rtt = 0;
      let jitter = 0;
      let packetsSent = 0;
      let packetsLost = 0;

      stats.forEach((report) => {
        if (report.kind === 'audio') {
          if (report.type === 'remote-inbound-rtp') {
            rtt = report.roundTripTime * 1e3;
            jitter = report.jitter * 1e3;
            packetsLost = report.packetsLost;
          }

          if (report.type === 'remote-outbound-rtp') {
            packetsSent = report.packetsSent;
          }
        }
      });

      let packetLoss = packetsSent && packetsSent ? Math.round((packetsLost * 100) / (packetsSent + packetsLost)) : 0;

      this.badConnectionQuality.next(rtt > 300 || jitter > 50 || packetLoss > 1);
    });
  }

  private clearCheckConnectionQuality() {
    this.badConnectionQuality.next(false);

    if (this.checkConnectionQualityTimeout) {
      clearInterval(this.checkConnectionQualityTimeout);
    }
  }

  private getErrorCause(data): string {
    const reasonString = data?.message?.headers?.Reason?.[0].raw || '';

    const match = reasonString.match(/cause=(\d+)/);
    const causeValue = match ? match[1] : null;

    if (!causeValue) {
      return SipErrorsE[SipErrorsE.NORMAL_CLEARING];
    }

    return causeValue && SipErrorsE[causeValue] ? SipErrorsE[causeValue] : SipErrorsE[SipErrorsE.CALL_FAILED];
  }

  public async getNumberRegion() {
    const phone = this.dialerStatus?.numberCall ?? '';
    const regions = this.pabxService.getNumberRegion(phone);

    if (regions) {
      firstValueFrom(regions).then((region) => {
        if (region.length > 0) {
          this.updateDialerStatus({ region: region[0].localidade });
        }
      });
    }
  }

  private async enableDebugLogs() {
    if (this.user?.customerId == SIP_LOG_ENABLED_CUSTOMER_ID) {
      debug.enable('JsSIP:*');
    }
  }

  private async emitSipLog(type: string, data: any = {}) {
    if (this.user?.customerId == SIP_LOG_ENABLED_CUSTOMER_ID) {
      this.callHistoryService.createSipLog(type, data, this.user as UserSocketLoginI);
    }
  }
}
